/**
 * 話者の表示名（話者N）と色分けの導出ユーティリティ。
 *
 * 生ラベル（例: speaker_0）を「検出順（セグメント出現順）」で表示名・色に割り当てる。
 * バックエンド segment_output_resolver.py と同一の検出順採番規則であり、画面と
 * ダウンロード出力の表示名（話者N）を一致させる。一致の根拠は両者が同一の DB
 * segments 配列順（GET API 応答の segments）を入力にすること。
 */
import type { Transcription, TranscriptSegment } from '../types';

export interface SpeakerColor {
  bg: string;
  border: string;
  text: string;
}

// 話者色パレット（8色・循環）。otomochi の配色トーンに調和し、識別しやすい配色。
// バックエンド HTML 出力（output_formatter の .speaker-color-N）と対応。
const SPEAKER_COLORS: SpeakerColor[] = [
  { bg: '#FDE2D8', border: '#de8f7d', text: '#9c4a39' },
  { bg: '#DDEBF3', border: '#5b8fb0', text: '#2f5a73' },
  { bg: '#E2EFD9', border: '#7fa86a', text: '#496a37' },
  { bg: '#F3E2EF', border: '#b06fa0', text: '#73396a' },
  { bg: '#FBEFD0', border: '#d8b15a', text: '#8a6a1f' },
  { bg: '#D9EFEC', border: '#5fa89c', text: '#356a61' },
  { bg: '#E6E0F2', border: '#8b7ab0', text: '#4f3f73' },
  { bg: '#F2E3DC', border: '#b08a7a', text: '#73503f' },
];

export interface SpeakerDisplay {
  rawLabel: string;
  displayName: string;
  colorIndex: number;
  color: SpeakerColor;
}

/** 色インデックスからパレット色を返す（循環）。 */
export function getSpeakerColor(colorIndex: number): SpeakerColor {
  return SPEAKER_COLORS[colorIndex % SPEAKER_COLORS.length];
}

/**
 * 生ラベル → 表示名・色 の写像を検出順で構築する。
 * 話者ラベルが無い（null/undefined/空）セグメントは写像に含めない。
 *
 * nameOverrides（生ラベル→ユーザー指定名・Unit 005 リネーム）が与えられた場合、
 * 検出済みの生ラベルに限り displayName をユーザー指定名で上書きする（空欄/空白のみは無視＝話者N 維持）。
 * colorIndex は上書きの有無にかかわらず検出順で不変（DR Q5: リネームは表示名のみ変更）。
 * バックエンド segment_output_resolver.py と同一規則（上書き優先・空欄は話者N）。
 */
export function getSpeakerDisplayMap(
  segments: TranscriptSegment[],
  nameOverrides?: Record<string, string> | null,
): Map<string, SpeakerDisplay> {
  const map = new Map<string, SpeakerDisplay>();
  for (const seg of segments) {
    const label = seg.speaker;
    if (!label) continue;
    if (!map.has(label)) {
      const colorIndex = map.size;
      const override = nameOverrides?.[label];
      const displayName =
        override && override.trim() ? override.trim() : `話者${map.size + 1}`;
      map.set(label, {
        rawLabel: label,
        displayName,
        colorIndex,
        color: getSpeakerColor(colorIndex),
      });
    }
  }
  return map;
}

/**
 * 凡例表示用。検出された話者を検出順で返す（話者なしなら空配列）。
 * nameOverrides があれば displayName に上書きを反映する。
 *
 * 注（Unit 007）: 凡例の母集合は **元 segments の生ラベル**であり、セグメント単位の話者付け替え
 * （segment_speaker_overrides）は母集合・採番・色に影響させない（バックエンドと同一規則）。
 * 付け替えで実効0セグメントになった話者も凡例に残る（番号・色は不変）。
 */
export function listDetectedSpeakers(
  segments: TranscriptSegment[],
  nameOverrides?: Record<string, string> | null,
): SpeakerDisplay[] {
  return Array.from(getSpeakerDisplayMap(segments, nameOverrides).values());
}

/**
 * セグメントの実効話者生ラベルを返す（Unit 007 手動修正の付け替えを反映）。
 *
 * segment_speaker_overrides に当該 index の付け替えがあればそれを、無ければ元の生ラベルを返す。
 * バックエンド segment_output_resolver.resolve_segments_for_output と同一規則（採番マップは
 * 元検出順固定・実効ラベルで lookup）。返した生ラベルを getSpeakerDisplayMap の結果で引くと表示名・色が得られる。
 */
export function getEffectiveSpeakerLabel(
  index: number,
  segment: TranscriptSegment,
  speakerOverrides?: Record<string, string> | null,
): string | null | undefined {
  const override = speakerOverrides?.[String(index)];
  return override ?? segment.speaker;
}

/**
 * セグメントの実効テキストを返す（Unit 007 手動修正のテキスト編集を反映）。
 * segment_text_overrides に当該 index の編集があればそれを、無ければ元テキストを返す。
 */
export function getEffectiveText(
  index: number,
  segment: TranscriptSegment,
  textOverrides?: Record<string, string> | null,
): string {
  return textOverrides?.[String(index)] ?? segment.text;
}

/**
 * 表示・出力に使う実効セグメント配列を返す（speaker は生ラベル）。
 * edited_segments（v3.8.0 分割）があればそれ、無ければ segments に Unit 007 の override を適用した配列。
 * バックエンド resolve_segments_for_output と同一規則（採番・色は引き続き元 segments から作る）。
 */
export function getEffectiveSegments(
  t: Pick<Transcription, 'segments' | 'segment_speaker_overrides' | 'segment_text_overrides' | 'edited_segments'>,
): TranscriptSegment[] {
  if (t.edited_segments) return t.edited_segments;
  return t.segments.map((seg, i) => ({
    ...seg,
    speaker: getEffectiveSpeakerLabel(i, seg, t.segment_speaker_overrides),
    text: getEffectiveText(i, seg, t.segment_text_overrides),
  }));
}

/**
 * セグメントを text の cursor 位置で2つに分ける（v3.8.0 分割）。
 * 時刻は元の時間を文字数比で振り分ける（小数3桁）。話者は両方とも元の話者を引き継ぐ。
 * 分けた片（前後の空白を除く）のどちらかが空になる位置なら null（分割不可）。
 */
export function splitSegment<T extends TranscriptSegment>(seg: T, cursor: number): [T, T] | null {
  const head = seg.text.slice(0, cursor).trim();
  const tail = seg.text.slice(cursor).trim();
  if (!head || !tail) return null;
  const ratioAt = Math.round((seg.start + ((seg.end - seg.start) * cursor) / seg.text.length) * 1000) / 1000;
  // 丸めで元の区間外に出ないようにする（サーバは start <= end と並び順を検証する）
  const at = Math.min(seg.end, Math.max(seg.start, ratioAt));
  return [
    { ...seg, end: at, text: head },
    { ...seg, start: at, text: tail },
  ];
}
